import React, { useEffect } from "react";
import { connect } from "react-redux";
import "./CouponSettingSection.scss"
import { SectionTopTitle } from "components/base/CommonComponent";
import ReactHookFormSwitch from "components/reactHookFormComponents/ReactHookFormSwitch";
import { useFormContext } from "react-hook-form";
import { COUPON_CODE_TYPE, COUPON_FORMATS, COUPON_REDEMPTION_METHOD, COUPON_SET_MEDIUM, COUPON_SET_TYPES, DISPLAY_COUPON_FORMATS, DISPLAY_COUPON_REDEMPTION_METHOD, DISPLAY_COUPON_SET_TYPES } from "config/CustomEnums";
import { CustomTitleLabel } from "components/earning/CustomBaseComponments";
import ReactHookFormTitleWithRadio from "components/reactHookFormComponents/ReactHookFormTitleWithRadio";
import ReactHookFormTextInput from "components/reactHookFormComponents/ReactHookFormTextInput";
import TypeSelector from "./TypeSelector";
import ImportExistingCouponImg from 'assets/images/coupon/importExistingCoupon.svg';
import SystemGeneratedImg from 'assets/images/coupon/systemGenerated.svg';
import CouponBarcodeSelectDropdown from "components/coupon/couponCreation/CouponBarcodeSelectDropdown";

const OnlineAndOfflineCouponSettingSection = ({
  couponSourceFieldName,
  couponRedemptionMethodFieldName,
  couponCodeFormatFieldName,
  couponCouponCodeTypeFieldName,
  couponCodeFieldName,
}) => {
  const { watch } = useFormContext();

  const couponCodeType = watch(couponCouponCodeTypeFieldName)

  return <div className="create-section-label-bottom-space">
    <SectionTopTitle title={"both online and offline coupons"} />
    <ReactHookFormTitleWithRadio
      title={"Coupon redemption method"}
      valueFieldName={couponRedemptionMethodFieldName}
      defaultValue={COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE}
      options={[
        { label: 'Sales system updated (e.g. POS)', value: COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE },
      ]}
    />
    <div>
      <CustomTitleLabel title={"Coupon source"} />
      <TypeSelector
        typeConfig={[
          {
            id: COUPON_SET_TYPES.IMPORT_EXISTING_COUPON,
            title: 'External',
            image: ImportExistingCouponImg,
            description:
              'You will need to import existing coupon codes (.csv) to the system',
          },
        ]}
        typeFieldName={couponSourceFieldName}
        defaultValue={COUPON_SET_TYPES.IMPORT_EXISTING_COUPON}
        customItemClassName='coupon-source-item'
        itemWidth={375}
      />
    </div>
    <ReactHookFormTitleWithRadio
      title={"Code format"}
      valueFieldName={couponCodeFormatFieldName}
      defaultValue={COUPON_FORMATS.TEXT}
      options={[
        {
          label: DISPLAY_COUPON_FORMATS.TEXT,
          value: COUPON_FORMATS.TEXT,
        },
      ]}
    />
    <ReactHookFormTitleWithRadio
      title={"Code string behaviour"}
      valueFieldName={couponCouponCodeTypeFieldName}
      defaultValue={COUPON_CODE_TYPE.GENERIC}
      options={[
        {
          label: 'All coupons have the same code',
          value: COUPON_CODE_TYPE.GENERIC,
        },
        {
          label: 'Each coupon has a unique code',
          value: COUPON_CODE_TYPE.UNIQUE,
        },
      ]}
    />
    {couponCodeType === COUPON_CODE_TYPE.GENERIC &&
      <ReactHookFormTextInput
        title="Code string"
        valueFieldName={couponCodeFieldName}
      />
    }
  </div>
}

const OnlineCouponSettingSection = ({
  couponSourceFieldName,
  couponRedemptionMethodFieldName,
  couponCodeFormatFieldName,
  couponCouponCodeTypeFieldName,
  couponCodeFieldName,
}) => {
  const { watch } = useFormContext();

  const couponCodeType = watch(couponCouponCodeTypeFieldName);

  return <div className="create-section-label-bottom-space">
    <SectionTopTitle title={"Online Coupons"} />
    <ReactHookFormTitleWithRadio
      title={"Coupon redemption method"}
      valueFieldName={couponRedemptionMethodFieldName}
      defaultValue={COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE}
      options={[
        { label: 'Sales system updated (e.g. POS)', value: COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE },
      ]}
    />
    <div>
      <CustomTitleLabel title={"Coupon source"} />
      <TypeSelector
        typeConfig={[
          {
            id: COUPON_SET_TYPES.IMPORT_EXISTING_COUPON,
            title: 'External',
            image: ImportExistingCouponImg,
            description:
              'You will need to import existing coupon codes (.csv) to the system',
          },
        ]}
        typeFieldName={couponSourceFieldName}
        defaultValue={COUPON_SET_TYPES.IMPORT_EXISTING_COUPON}
        customItemClassName='coupon-source-item'
        itemWidth={375}
      />
    </div>
    <ReactHookFormTitleWithRadio
      title={"Code format"}
      valueFieldName={couponCodeFormatFieldName}
      defaultValue={COUPON_FORMATS.TEXT}
      options={[
        {
          label: DISPLAY_COUPON_FORMATS.TEXT,
          value: COUPON_FORMATS.TEXT,
        },
      ]}
    />
    <ReactHookFormTitleWithRadio
      title={"Code string behaviour"}
      valueFieldName={couponCouponCodeTypeFieldName}
      defaultValue={COUPON_CODE_TYPE.GENERIC}
      options={[
        {
          label: 'All coupons have the same code',
          value: COUPON_CODE_TYPE.GENERIC,
        },
        {
          label: 'Each coupon has a unique code',
          value: COUPON_CODE_TYPE.UNIQUE,
        },
      ]}
    />
    {couponCodeType === COUPON_CODE_TYPE.GENERIC &&
      <ReactHookFormTextInput
        title="Code string"
        valueFieldName={couponCodeFieldName}
      />
    }
  </div>
}

const OfflineCouponSettingSection = ({
  couponSourceFieldName,
  couponRedemptionMethodFieldName,
  couponCodeFormatFieldName,
  couponCouponCodeTypeFieldName,
  couponCodeFieldName,
  offlineShowCopiableStringFieldName,
  offlineBarcodeFieldName,
}) => {
  const { watch, setValue } = useFormContext();

  const couponCodeType = watch(couponCouponCodeTypeFieldName);
  const couponSource = watch(couponSourceFieldName)
  const couponRedemptionMethod = watch(couponRedemptionMethodFieldName)
  const codeFormat = watch(couponCodeFormatFieldName)
  const offlineBarcode = watch(offlineBarcodeFieldName)

  useEffect(()=> {
    if (couponRedemptionMethod === COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE) {
      setValue(couponSourceFieldName, COUPON_SET_TYPES.IMPORT_EXISTING_COUPON, { shouldDirty: true })
    }
  }, [couponRedemptionMethod])

  return <div className="create-section-label-bottom-space">
    <SectionTopTitle title={"Offline Coupons"} />
    <ReactHookFormTitleWithRadio
      title={"Coupon redemption method"}
      valueFieldName={couponRedemptionMethodFieldName}
      defaultValue={COUPON_REDEMPTION_METHOD.MERCHANT_APP_SCAN_COUPON_QR_CODE}
      options={[
        COUPON_REDEMPTION_METHOD.MERCHANT_APP_SCAN_COUPON_QR_CODE,
        COUPON_REDEMPTION_METHOD.USERS_SCAN_STORE_QR_CODE,
        COUPON_REDEMPTION_METHOD.INPUT_STORE_STAFF_CODE,
        COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE,
      ]?.map((item)=> ({
        label: DISPLAY_COUPON_REDEMPTION_METHOD?.[item] || item, value: item
      }))}
    />
    <div>
      <CustomTitleLabel title={"Coupon source"} />
      <TypeSelector
        typeConfig={[
          ...couponRedemptionMethod === COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE ? [] : [
            {
              id: COUPON_SET_TYPES.SYSTEM_GENERATED,
              title: DISPLAY_COUPON_SET_TYPES?.[COUPON_SET_TYPES.SYSTEM_GENERATED],
              image: SystemGeneratedImg,
              description:
                'You will need to input the quantity of coupons to be generated (per store)',
            },
          ],
          {
            id: COUPON_SET_TYPES.IMPORT_EXISTING_COUPON,
            title: DISPLAY_COUPON_SET_TYPES?.[COUPON_SET_TYPES.IMPORT_EXISTING_COUPON],
            image: ImportExistingCouponImg,
            description:
              'You will need to import existing coupon codes (.csv) to the system',
          },
        ]}
        typeFieldName={couponSourceFieldName}
        defaultValue={COUPON_SET_TYPES.SYSTEM_GENERATED}
        customItemClassName='coupon-source-item'
        itemWidth={375}
      />
    </div>
    {couponSource === COUPON_SET_TYPES.IMPORT_EXISTING_COUPON ? <>
      <ReactHookFormTitleWithRadio
      title={"Code format"}
      valueFieldName={couponCodeFormatFieldName}
      defaultValue={COUPON_FORMATS.TEXT}
      options={[
        {
          label: DISPLAY_COUPON_FORMATS.COUPON_QR_CODE,
          value: COUPON_FORMATS.COUPON_QR_CODE,
        },
        {
          label: DISPLAY_COUPON_FORMATS.COUPON_BAR_CODE,
          value: COUPON_FORMATS.COUPON_BAR_CODE,
        },
        {
          label: DISPLAY_COUPON_FORMATS.TEXT,
          value: COUPON_FORMATS.TEXT,
        },
      ]}
      />
      {codeFormat === COUPON_FORMATS.COUPON_BAR_CODE ?
        <CouponBarcodeSelectDropdown
          title={'Barcode format'}
          defaultValue={{
            label: offlineBarcode?.name,
            value: offlineBarcode,
          }}
          onSelectChange={(value) => {
            setValue(offlineBarcodeFieldName, value, { shouldDirty: true });
          }}
        /> : null
      }

    <ReactHookFormTitleWithRadio
      title={"Code string behaviour"}
      valueFieldName={couponCouponCodeTypeFieldName}
      defaultValue={COUPON_CODE_TYPE.GENERIC}
      options={[
        {
          label: 'All coupons have the same code',
          value: COUPON_CODE_TYPE.GENERIC,
        },
        {
          label: 'Each coupon has a unique code',
          value: COUPON_CODE_TYPE.UNIQUE,
        },
      ]}
    />
    {couponCodeType === COUPON_CODE_TYPE.GENERIC &&
      <ReactHookFormTextInput
        title="Code string"
        valueFieldName={couponCodeFieldName}
      />
    }
      {couponRedemptionMethod === COUPON_REDEMPTION_METHOD.POS_UPDATED_CODE &&
        <ReactHookFormSwitch
          title="Show copy code button"
          valueFieldName={offlineShowCopiableStringFieldName}
          defaultTrue={false}
        />
      }
    </> : null}
  </div>
}

const CouponSettingSection = ({
  fieldNamePrefix,
  couponSetMedium = COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE, // type: COUPON_SET_MEDIUM
}) => {
  const { watch, setValue } = useFormContext();

  const setCouponPropertiesSeparatelyFieldName = `${fieldNamePrefix}isDifferentCodeForOnlineOffline`
  const setCouponPropertiesSeparately = watch(setCouponPropertiesSeparatelyFieldName)

  const onlineAndOffineFieldPrefix = `${fieldNamePrefix}onlineAndOffline`
  const onlineFieldPrefix = `${fieldNamePrefix}online`
  const offlineFieldPrefix = `${fieldNamePrefix}offline`

  const couponSourceFieldName = `CouponSetType`;
  const couponRedemptionMethodFieldName = `RedemptionMethod`;
  const couponCodeFormatFieldName = `Formats`;
  const couponCouponCodeTypeFieldName = `CouponCodeType`;
  const couponCodeFieldName = `GenericCode`;

  const onlineOfflineCouponCodeType = watch(`${onlineAndOffineFieldPrefix}${couponCouponCodeTypeFieldName}`)
  const onlineOfflineCouponCode = watch(`${onlineAndOffineFieldPrefix}${couponCodeFieldName}`)

  useEffect(()=>{
    if (couponSetMedium === COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE && !setCouponPropertiesSeparately) {
      setValue(`${offlineFieldPrefix}${couponCouponCodeTypeFieldName}`, onlineOfflineCouponCodeType, { shouldDirty: true });
      setValue(`${onlineFieldPrefix}${couponCouponCodeTypeFieldName}`, onlineOfflineCouponCodeType, { shouldDirty: true });
      setValue(`${offlineFieldPrefix}${couponCodeFieldName}`, onlineOfflineCouponCode, { shouldDirty: true });
      setValue(`${onlineFieldPrefix}${couponCodeFieldName}`, onlineOfflineCouponCode, { shouldDirty: true });
    }
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponSetMedium, setCouponPropertiesSeparately, onlineOfflineCouponCodeType, onlineOfflineCouponCode])

  return <div>
    <SectionTopTitle title={"COUPON PROPERTIES"} />
    {couponSetMedium === COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE &&
      <ReactHookFormSwitch
        title={"Set properties for online & offline coupons separately"}
        valueFieldName={setCouponPropertiesSeparatelyFieldName}
        defaultTrue={false}
      />
    }
    <div className="split-dashed-line create-section-label-bottom-space" />

    {couponSetMedium === COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE && !setCouponPropertiesSeparately ?
      <OnlineAndOfflineCouponSettingSection
        couponSourceFieldName={`${onlineAndOffineFieldPrefix}${couponSourceFieldName}`}
        couponRedemptionMethodFieldName={`${onlineAndOffineFieldPrefix}${couponRedemptionMethodFieldName}`}
        couponCodeFormatFieldName={`${onlineAndOffineFieldPrefix}${couponCodeFormatFieldName}`}
        couponCouponCodeTypeFieldName={`${onlineAndOffineFieldPrefix}${couponCouponCodeTypeFieldName}`}
        couponCodeFieldName={`${onlineAndOffineFieldPrefix}${couponCodeFieldName}`}
      /> : null
    }
    {(couponSetMedium === COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE && setCouponPropertiesSeparately)
      || couponSetMedium === COUPON_SET_MEDIUM.ONLINE ?
      <OnlineCouponSettingSection
        couponSourceFieldName={`${onlineFieldPrefix}${couponSourceFieldName}`}
        couponRedemptionMethodFieldName={`${onlineFieldPrefix}${couponRedemptionMethodFieldName}`}
        couponCodeFormatFieldName={`${onlineFieldPrefix}${couponCodeFormatFieldName}`}
        couponCouponCodeTypeFieldName={`${onlineFieldPrefix}${couponCouponCodeTypeFieldName}`}
        couponCodeFieldName={`${onlineFieldPrefix}${couponCodeFieldName}`}
      /> : null
    }
    {couponSetMedium === COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE && setCouponPropertiesSeparately ? 
      <div className="split-dashed-line create-section-label-bottom-space" /> : null
    }
    {(couponSetMedium === COUPON_SET_MEDIUM.ONLIEN_OR_OFFLINE && setCouponPropertiesSeparately)
      || couponSetMedium === COUPON_SET_MEDIUM.OFFLINE ?
      <OfflineCouponSettingSection
        couponSourceFieldName={`${offlineFieldPrefix}${couponSourceFieldName}`}
        couponRedemptionMethodFieldName={`${offlineFieldPrefix}${couponRedemptionMethodFieldName}`}
        couponCodeFormatFieldName={`${offlineFieldPrefix}${couponCodeFormatFieldName}`}
        couponCouponCodeTypeFieldName={`${offlineFieldPrefix}${couponCouponCodeTypeFieldName}`}
        couponCodeFieldName={`${offlineFieldPrefix}${couponCodeFieldName}`}
        offlineShowCopiableStringFieldName={`${offlineFieldPrefix}ShowCopiableString`}
        offlineBarcodeFieldName={`${offlineFieldPrefix}BarcodeFormat`}
      /> : null
    }

  </div>
}

const mapPropsToState = () => ({
});

export default connect(mapPropsToState)(CouponSettingSection);